import type { ApiResource } from "../../shared/api/resource.js";
import { stringArrayField } from "../../shared/api/resource.js";

export interface TickerView {
  readonly backtesting: {
    readonly coverageFrom?: string;
    readonly coverageTo?: string;
    readonly intervals: readonly string[];
    readonly issues: readonly string[];
    readonly state: string;
  };
  readonly botCount: number;
  readonly live: {
    readonly freshness?: string;
    readonly intervals: readonly string[];
    readonly issues: readonly string[];
    readonly lastBarAt?: string;
    readonly provider: string;
    readonly sessionState: string;
    readonly state: string;
  };
  readonly states: readonly string[];
  readonly symbol: string;
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function optionalText(value: unknown): string | undefined {
  const result = text(value);
  return result === "" ? undefined : result;
}

function strings(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

export function tickerView(resource: ApiResource): TickerView {
  const live = record(resource.live);
  const backtesting = record(resource.backtesting);
  const coverage = record(backtesting.coverage);
  const coverageFrom = optionalText(backtesting.coverageFrom ?? coverage.from);
  const coverageTo = optionalText(backtesting.coverageTo ?? coverage.to);
  const freshness = optionalText(live.freshness);
  const lastBarAt = optionalText(live.lastBarAt);
  return {
    backtesting: {
      ...(coverageFrom === undefined ? {} : { coverageFrom }),
      ...(coverageTo === undefined ? {} : { coverageTo }),
      intervals: strings(backtesting.intervals ?? resource.backtestIntervals),
      issues: strings(backtesting.issues),
      state: text(backtesting.state, "unavailable"),
    },
    botCount:
      typeof resource.botCount === "number" && Number.isFinite(resource.botCount)
        ? resource.botCount
        : 0,
    live: {
      ...(freshness === undefined ? {} : { freshness }),
      intervals: strings(live.intervals ?? resource.liveIntervals),
      issues: strings(live.issues),
      ...(lastBarAt === undefined ? {} : { lastBarAt }),
      provider: text(live.provider, "Alpaca"),
      sessionState: text(live.sessionState, "unavailable"),
      state: text(live.state, "unavailable"),
    },
    states: stringArrayField(resource, "states"),
    symbol: text(resource.symbol, resource.id),
  };
}
